/* eslint-disable no-param-reassign */
/* eslint-disable no-continue */
import React, { useState, useRef, useLayoutEffect, useEffect, useMemo } from 'react';
import './utils/i18n';
import { debounce } from 'ts-debounce';
import { Layout, Responsive } from 'react-grid-layout';
import WidthProviderRGL from './utils/WidthProvider';
import ModalsOutlet from './components/elements/Modals/Modals';
import CPTile from './components/PCTile';
import { ModalContextProvider, useModals } from './contexts/modalContext';
import { UserTileContextProvider, useTiles } from './contexts/userTilesContext';
import { ICockpitProps, IModuleTile, IModules, ILinkedIconModuleProps, IUserSettings } from './interfaces/interfaces';
import { pcMockDataAuthenticated, simpleLayout } from './mock_data/mock-data';
import {
  getGQLQueryString,
  initI18n,
  loadGQLData,
  loadUserConfig,
  mergeTilesWithConfig,
  saveConfiguration,
} from './utils/data';
import keyboardTriggerBasic from './utils/keyboard-trigger';
import { availableModules } from './modules';
import ModalHelpers from './helpers/ModalHelpers/ModalHelpers';
import getNextFreeSlot from './utils/get-next-free-slot';
import { recompPositions, sortedByPosition, breakpoints, colsConfig, getBreakpointForWidth } from './utils/gridUtils';

const ResponsiveGridLayout = WidthProviderRGL(Responsive);

const PartnerportalCockpit = (props: ICockpitProps): JSX.Element => {
  const { jsonConfig } = props;
  const [config] = useState(JSON.parse(jsonConfig));
  const [showError, setShowError] = useState<string>('');

  const isDev = config.isDev || false;
  const useMockData = config.useMockData || false;
  const hasUser = config.user || false;
  // using given data for preview in FS instead of using API or mockData
  const previewConfig = config.previewConfiguration || null;
  const previewTiles = config.previewTiles || null;
  const debouncedSaveConfiguration = debounce(saveConfiguration, 200);
  /* if required somehow for debugging: */
  // const isLocal = !!document.querySelector('#viewport-hint');

  /* === GRID === */
  const frameRef = useRef<HTMLDivElement>(null);
  const [rowHeight, setRowHeight] = useState(200);
  const [currentBreakpoint, setCurrentBreakpoint] = useState<string>('lg');
  const [calculatedBreakpoint, setCalculatedBreakpoint] = useState<string>('lg');
  const [oldLayout, setOldLayout] = useState<Layout[] | null>(null);
  const [oldTilesAdoptedLayout, setOldTilesAdoptedLayout] = useState<IModuleTile[]>([]);

  /* === MODALS === */

  const [modalData, openModal, closeModal] = useModals();

  const openOnboarding = () => {
    ModalHelpers.openOnboarding(openModal);
  };

  const openThemenauswahl = () => {
    ModalHelpers.openPersonalisation(openModal, isDev);
  };

  /* === TILES === */

  const [
    tiles,
    addTile,
    removeTile,
    setTiles,
    modules,
    setModules,
    isLoaded,
    setIsLoaded,
    settings,
    setSettings,
    refTiles,
    setRefTiles,
  ] = useTiles();

  const fakeAddTile = (w: number, h: number, moduleConf?: IModuleTile) => {
    const tileId = `new${Math.random() * 1000}`;
    const nextFreeSlot = getNextFreeSlot(tiles, w);

    if (moduleConf) {
      addTile({ ...moduleConf, layout: { i: 'ss', x: nextFreeSlot.x, y: nextFreeSlot.y, w, h } });
      return;
    }

    addTile({
      id: tileId,
      module: 'LinkedIconModule',
      props: {
        headline: `Tile-${tileId}`,
        textContent: '',
        tileId,
        linkText: 'Link',
        linkHref: '#',
      } as ILinkedIconModuleProps,
      layout: { i: 'ss', x: nextFreeSlot.x, y: nextFreeSlot.y, w, h },
    });
  };

  const sortedTiles = sortedByPosition(tiles);
  const tilesWithId1Col = [
    // remap positions to 1x grid
    ...recompPositions(sortedTiles, 1, currentBreakpoint, availableModules),
  ];

  const tilesWithId2Col = [
    // remap positions to 2x grid
    ...recompPositions(sortedTiles, 2, currentBreakpoint, availableModules),
  ];

  const tilesWithId3Col = [
    // remap positions to 3x grid
    ...recompPositions(sortedTiles, 3, currentBreakpoint, availableModules),
  ];

  const tilesWithId4Col = [
    // remap positions to 4x grid
    ...recompPositions(sortedTiles, 4, currentBreakpoint, availableModules),
  ];

  const loadAvailableModules = () => {
    // We keep it static!
    return Promise.resolve(availableModules);
  };

  const updateStatic = (tilesToUpdate: IModuleTile[]) => {
    return tilesToUpdate.map((t) => {
      return { ...t, layout: { ...t.layout, static: hasUser ? t.layout.static || false : true } };
    });
  };

  const loadUserTiles = () => {
    return new Promise((resolve, reject) => {
      // load user config (tileset)
      loadUserConfig(isDev)
        .then((fetchedUserConfig) => {
          // preare refIds to fetch tiles for
          const tileIdList =
            fetchedUserConfig.tileList.map((ut: IModuleTile) => {
              return (ut.tileRefId || '').toString();
            }) || [];
          // fetch tiles for refIds
          loadGQLData(getGQLQueryString(tileIdList), isDev)
            .then((data) => {
              if (data.dictionary) {
                /* test-translations: */
                // data.dictionary.dictionaryItems.forEach((item)=>{item.value='[['+item.value+']]'})
                initI18n(data.dictionary.dictionaryItems);
              }
              setRefTiles(data.tiles);
              const uTiles = data as { tiles: IModuleTile[] };
              // merge userTileConfig with refTileConfig
              const loadedTiles = mergeTilesWithConfig(fetchedUserConfig.tileList, uTiles.tiles);
              setRefTiles(uTiles.tiles);
              // resolve tiles - finish
              resolve({ loadedTiles, settings: fetchedUserConfig.config.settings });
            })
            .catch((e) => {
              // eslint-disable-next-line no-console
              console.error(e);
              reject(new Error('G01'));
            });
        })
        .catch(() => {
          reject(new Error('R01'));
        });
    });
  };

  const initData = () => {
    if (useMockData) {
      // use mock data:
      loadAvailableModules().then((loadedModules) => {
        setModules(loadedModules as IModules);
        // set 'tile.layout.static=true' for not logged in users
        const staticTileset = updateStatic(sortedByPosition(pcMockDataAuthenticated.tiles as IModuleTile[]));
        setTiles(staticTileset);
        setIsLoaded(true);
      });
      return;
    }

    if (previewConfig !== null) {
      loadAvailableModules().then((loadedModules) => {
        setModules(loadedModules as IModules);
        setTiles(updateStatic(mergeTilesWithConfig(previewConfig, previewTiles)));
        setIsLoaded(true);
      });
      return;
    }

    // initialize data - API
    loadAvailableModules().then((loadedModules) => {
      setModules(loadedModules as IModules);

      loadUserTiles()
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((userData: any) => {
          // set 'tile.layout.static=true' for not logged in users
          const staticTileset = updateStatic(userData.loadedTiles as IModuleTile[]);
          setTiles(staticTileset);
          const loadedUserSettings = userData.settings || ({ didFinishOnboarding: false } as IUserSettings);
          setSettings(loadedUserSettings);
          setIsLoaded(true);

          if (config.user && loadedUserSettings.didFinishOnboarding !== true) {
            openOnboarding();
          }
        })
        .catch((e) => {
          setShowError(`Daten konnten nicht geladen werden. (${e})`);
          setIsLoaded(true);
        });
    });
  };


  const attachGlobalEventListeners = () => {
    document.addEventListener('ppc-open-personalization', openThemenauswahl);
  };

  useEffect(() => {
    initData();
    attachGlobalEventListeners();
  }, []);

  /* === LAYOUT === */

  const resizeTiles = () => {
    const gridWidth = frameRef?.current?.clientWidth;
    if (gridWidth) {
      setRowHeight(gridWidth / colsConfig[currentBreakpoint] - 25);
    }

    // IDEA TO SCALE TILES PROPOTIONALLY

    // const f = frameRef?.current?.clientWidth || 1080  / 1080;
    // (document.querySelectorAll('.tile-wrapper')).forEach((tw)=>{
    //   tileWrapper.style.transform = `scale(${f})`;
    //   tileWrapper.style.transformOrigin = '0 0'
    // })
  };

  // SAVE HAPPENS HERE
  const updateTileLayouts = (layouts: Layout[]) => {
    const cBreakpoint = getBreakpointForWidth(window.innerWidth); // recalculate because state is too slow
    const adoptPosition = ['xl'].indexOf(cBreakpoint) > -1;
    if (!oldLayout) {
      setOldLayout(layouts);
      return;
    }

    const tilesWithAdoptedLayout = [
      ...tiles.map((tile) => {
        let [layout] = layouts.filter((l) => l.i === tile.id);
        if (!layout) {
          layout = tile.layout;
        }

        return {
          ...tile,
          /* adopting changed layout */
          layout:
            {
              ...tile.layout,
              x: !adoptPosition ? tile.layout.x : layout.x,
              y: !adoptPosition ? tile.layout.y : layout.y,
              w: !adoptPosition ? tile.layout.w : layout.w,
              h: !adoptPosition ? tile.layout.h : layout.h,
            } || tile.layout,
        };
      }),
    ];

    // EQUAL CHECK
    const tilesAreEqual = JSON.stringify(tilesWithAdoptedLayout) === JSON.stringify(tiles);
    const tileLengthHasChanged =
      JSON.stringify(tilesWithAdoptedLayout.map((it) => it.id)) !==
      JSON.stringify(oldTilesAdoptedLayout.map((it) => it.id));

    setOldLayout(layouts);
    setOldTilesAdoptedLayout(tilesWithAdoptedLayout);

    if (tilesAreEqual && !tileLengthHasChanged) {
      // Nothing has changed - no need to save
      return;
    }

    // add resizable + static ONLY to tiles, dont save them
    const internalTilesWithAdoptedLayout = tilesWithAdoptedLayout.map((tile) => {
      return {
        ...tile,
        /* adopting changed layout */
        layout:
          {
            ...tile.layout,
            isResizable: hasUser ? tile.layout.isResizable || false : false,
            static: hasUser ? tile.layout.static || false : true,
          } || tile.layout,
      };
    });
    setTiles(internalTilesWithAdoptedLayout);

    // here we can persist it to somewhere...
    if (!hasUser) {
      return;
    }
    if (!useMockData) {
      debouncedSaveConfiguration(tilesWithAdoptedLayout, settings);
    }
  };

  const onLayoutChange = (layouts: Layout[]) => {
    resizeTiles();
    updateTileLayouts(layouts);
  };

  const updateCurrentBreakpoint = (bp: 'xl' | 'lg' | 'md' | 'sm' | 'xs' | 'xxs') => {
    setCurrentBreakpoint(bp);
    resizeTiles();
  };

  useLayoutEffect(() => {
    function onResize() {
      resizeTiles();
      const bp = getBreakpointForWidth(window.innerWidth);
      setCurrentBreakpoint(bp);
      setCalculatedBreakpoint(bp);
    }
    window.addEventListener('resize', onResize);
    onResize();

    if (isDev) {
      // grid css is loaded externally and the size is calculated before external css is modifying grid padding
      // therefore, we trigger 'resize' event to make the grid recalculate
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent('resize'));
      }, 500);
    }

    return () => {
      window.removeEventListener('resize', onResize);
    };
  });

  const children = useMemo(() => {
    return tiles.map((tile: IModuleTile) => {
      return (
        <div key={tile.id} className="tile-wrap">
          <CPTile tile={tile} modules={modules} hideMenu={!hasUser} />
        </div>
      );
    });
  }, [tiles]);

  let debugButtonBar;
  if (isDev) {
    debugButtonBar = (
      <div>
        {/* TEST BUTTON : ADD TILE */}
        <button type="button" onClick={() => fakeAddTile(1, 1)} style={{ marginTop: '30px' }}>
          + add dummy tile (1x1)
        </button>
        <button type="button" onClick={() => fakeAddTile(2, 1)} style={{ marginTop: '30px' }}>
          + add dummy tile (2x1)
        </button>
        <button type="button" onClick={() => fakeAddTile(3, 1)} style={{ marginTop: '30px' }}>
          + add dummy tile (3x1)
        </button>

        <button
          type="button"
          onClick={() => {
            const updatedSettings = { ...settings, didFinishOnboarding: false };
            setSettings(updatedSettings);
            saveConfiguration(tiles, updatedSettings);
            openOnboarding();
          }}
        >
          Onboarding
        </button>

        <button
          type="button"
          onClick={() => {
            openThemenauswahl();
          }}
        >
          Personalization/Themenauswahl
        </button>

        <button
          type="button"
          onClick={() => {
            let [refTile] = Object.values(refTiles).filter((rt) => rt.module === 'ContactModule');

            if (!refTile && !useMockData) {
              return;
            }

            if (useMockData) {
              refTile = { tileRefId: '4122', props: { headline: 'Kontakt' } } as IModuleTile;
            }

            fakeAddTile(1, 1, {
              id: `new${Math.random() * 1000}`,
              module: 'ContactModule',
              tileRefId: refTile.tileRefId || '',
              layout: simpleLayout(`t-${Math.random() * 1000}`, 1, 1, 1, 1, false),
              props: {
                headline: refTile.props.headline || '',
                tileId: '',
                textContent: '',
              },
            });
          }}
          style={{ marginTop: '30px' }}
        >
          + add ContactTile
        </button>
      </div>
    );
  }

  /* === RENDER === */

  if (!isLoaded) {
    return <div className="loading">Wird geladen...</div>;
  }

  if (showError) {
    return (
      <div className="api-error">
        {showError}
        <br />
        <a
          role="button"
          onClick={() => {
            window.location.reload();
          }}
          onKeyPress={(e) => {
            keyboardTriggerBasic(e, () => {
              window.location.reload();
            });
          }}
          tabIndex={0}
        >
          Erneut versuchen
        </a>
      </div>
    );
  }

  const breakpointLayouts = {
    xl: tilesWithId4Col,
    lg: tilesWithId3Col,
    md: tilesWithId3Col,
    sm: tilesWithId2Col,
    xs: tilesWithId1Col,
  };

  return (
    <>
      <div className="debug-info" style={{ position: 'relative' }}>
        BP: {currentBreakpoint} {calculatedBreakpoint}
      </div>

      <div ref={frameRef}>
        <UserTileContextProvider
          value={{ tiles, addTile, removeTile, setTiles, modules, settings, setSettings, refTiles, setRefTiles }}
        >
          <ModalContextProvider value={{ modals: [], openModal, closeModal }}>
            <ResponsiveGridLayout
              className="layout"
              layouts={breakpointLayouts}
              compactType="vertical"
              breakpoints={breakpoints}
              breakpoint={calculatedBreakpoint || 'lg'}
              cols={colsConfig}
              draggableHandle=".draggable"
              containerPadding={[0, 0]}
              margin={[35, 35]}
              rowHeight={rowHeight}
              onLayoutChange={onLayoutChange}
              onBreakpointChange={updateCurrentBreakpoint}
            >
              {children}
            </ResponsiveGridLayout>

            {debugButtonBar}

            <ModalsOutlet modalData={modalData} />
          </ModalContextProvider>
        </UserTileContextProvider>
      </div>
    </>
  );
};

export default PartnerportalCockpit;
